<template>
    <li
        :key="certification.index"
        :value="certification.index"
        class="profile-certifications__list-item"
        :class="{'draggable-certification': editMode, sample: isSample}"
    >
        <VJoyWrapper v-if="!isSample && editMode" class="action-edit" column-gap="4" justify="space-between" wrap="nowrap" item-stretch no-margin>
            <VJoyIconButton variant="ghost" size="small" icon="pen" :label="$t('general.edit')" @click="openEdit(certification.index)" />
            <VJoyDialogTrigger :dialog="removeDialogRef" @click="emitRemoval(certification.id!)">
                <VJoyIconButton v-if="editMode" variant="ghost" size="small" icon="trash" class="toolbar-remove" :label="$t('general.delete')" />
            </VJoyDialogTrigger>
            <VJoyIcon size="xsmall" name="menu" class="draggable-handle" />
        </VJoyWrapper>
        <VJoyWrapper align="flex-start" column-gap="0" wrap="nowrap" no-margin>
            <VJoyIcon size="large" class="profile-certifications__list-item__icon" name="file-check" />
            <div class="profile-certifications__list-item__main-content" :class="{'profile-certifications__list-item__main-content-edit': editMode}">
                <div class="profile-certifications__list-item__main-content-title">{{ certification.title }}</div>
                <div class="profile-certifications__list-item__main-content-subtitle">{{ certification.organization }}</div>
                <VJoyWrapper
                    v-if="certification.graduationYear"
                    class="profile-certifications__list-item__main-content-subtitle-calendar"
                    column-gap="2"
                    no-margin
                >
                    <small>{{ certification.graduationYear }}</small>
                </VJoyWrapper>
                <div v-if="certification.link" class="profile-certifications__list-item__main-content-link">
                    <VJoyLink :href="certificationLinkSanitized">{{ certification.link }}</VJoyLink>
                </div>
                <VJoyTagsList :tags="skills" variant="secondary" />
            </div>
        </VJoyWrapper>
    </li>
</template>

<script setup lang="ts">
    import {computed, toRef} from 'vue';
    import {isValidUrl} from '@malt/common';
    import type {TJoyTagsList, VJoyDialog} from '@maltjoy/core-vue';
    import {VJoyIconButton, VJoyDialogTrigger, VJoyIcon, VJoyLink, VJoyTagsList, VJoyWrapper} from '@maltjoy/core-vue';
    import type {CertificationRequest} from '~/types/infra/CertificationRequest';

    type Props = {
        modelValue: CertificationRequest;
        isSample: boolean;
        isEditMode: boolean;
        removeDialogRef?: InstanceType<typeof VJoyDialog> | null;
    };

    const props = defineProps<Props>();
    type Emits = {
        (e: 'remove', certificationToRemoveId: string): void;
        (e: 'edit', index: number): void;
    };
    const emit = defineEmits<Emits>();

    // Refs
    const certification = toRef(props, 'modelValue');
    const editMode = toRef(props, 'isEditMode');

    // Computed
    const certificationLinkSanitized = computed(() => (isValidUrl(certification.value?.link) ? certification.value?.link : ''));
    const skills = computed((): TJoyTagsList =>
        certification.value.skills.map((skill, index) => ({value: `certification-skill--${index}`, label: skill.label})),
    );

    function emitRemoval(certificationToRemoveId: string) {
        emit('remove', certificationToRemoveId);
    }

    function openEdit(index: number) {
        emit('edit', index);
    }
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .sample {
        opacity: 0.3;
    }

    .draggable-handle {
        cursor: grab;
        margin: var(--joy-core-spacing-3);

        // Tweak to ease the drag & drop on mobile
        @media screen and (max-width: 600px) {
            position: relative;

            &::after {
                content: '';
                position: absolute;
                inset: calc(-1 * var(--joy-core-spacing-3));
            }
        }
    }

    .toolbar-remove :deep(.joy-icon),
    .toolbar-remove:hover :deep(.joy-icon) {
        color: var(--joy-color-error-50) !important; // I know
    }

    .action-edit {
        position: absolute;
        right: 0;

        @media screen and (max-width: 600px) {
            display: flex;
            flex-direction: column;
            gap: var(--joy-core-spacing-4);
        }
    }

    .profile-certifications__list-item {
        position: relative;

        &:not(:first-child) {
            padding-top: var(--joy-core-spacing-6);
        }

        &:not(:last-child) {
            padding-bottom: var(--joy-core-spacing-6);
            border-bottom: 1px solid var(--joy-color-neutral-30);
        }

        &__icon {
            @include mq.screen_xs {
                display: none;
            }
        }

        &__main-content {
            margin-left: var(--joy-core-spacing-4);
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            width: 100%;

            &-edit {
                @media screen and (max-width: 600px) {
                    justify-content: flex-start;
                    min-height: 152px; // Buttons size + spacing
                    width: 80%;
                }

                .profile-certifications__list-item__main-content-title {
                    width: 70%;

                    @media screen and (max-width: 600px) {
                        width: 100%;
                    }
                }
            }

            &-title {
                margin-bottom: var(--joy-core-spacing-1);
                font-family: var(--joy-font-family-base);
                font-weight: var(--joy-font-weight-bold);
                font-size: var(--joy-font-size-primary-600);
                line-height: var(--joy-line-height-large);
                word-break: normal;
                overflow-wrap: anywhere;
                text-align: left;
            }

            &-subtitle {
                font-size: var(--joy-font-size-primary-300);
                margin-bottom: var(--joy-core-spacing-1);
                color: var(--joy-color-text-default);
                display: flex;
                word-break: normal;
                overflow-wrap: anywhere;
                text-align: left;

                &-calendar {
                    color: var(--joy-color-neutral-40);
                    margin-bottom: var(--joy-core-spacing-1);
                }
            }

            &-draggable {
                text-align: right;
                position: absolute;
                max-width: calc(40% - 20px);
                top: 0;
                right: 0;
            }

            &-link {
                word-break: normal;
                overflow-wrap: anywhere;
                text-align: left;
                margin-bottom: var(--joy-core-spacing-1);
            }
        }
    }
</style>
